import React, { useState } from "react";
import {
  Typography,
  Box,
  RadioGroup,
  FormControlLabel,
  Radio,
} from "@mui/material";
import DialogLayout from "../../DialogLayout";
import ButtonField from "../../Ui/ButtonField";
import { useTailwindBrand } from "../../../Theme/useTailwindBrand";
import { pictureAward } from "../../../Utils/Images";

const LEVEL_ORDINALS: Record<number, string> = {
  1: "premier",
  2: "deuxième",
  3: "troisième",
  4: "quatrième",
  5: "cinquième",
  6: "sixième",
  7: "dernier",
};

interface RewardDialogProps {
  open: boolean;
  onClose: () => void;
  currentLevelNum: number;
  currentLevel?: { name: string; emoji: string };
  rewardPdfUrl?: string;
  onNext?: () => void;
}

const RewardDialog: React.FC<RewardDialogProps> = ({
  open,
  onClose,
  currentLevelNum,
  currentLevel,
  rewardPdfUrl,
  onNext,
}) => {
  const { brandConfig } = useTailwindBrand();
  const [selectedReward, setSelectedReward] = useState("");

  const footer =
    currentLevelNum === 7 ? (
      <Typography
        variant="body2"
        fontWeight={700}
        sx={{ fontFamily: brandConfig.fontFamily, color: brandConfig.colors.primary }}
      >
        🎊 Bravo, vous avez complété tous les niveaux Teach'r !
      </Typography>
    ) : (
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", width: "100%", gap: 2 }}>
        <Typography
          variant="body2"
          fontWeight={600}
          sx={{ fontFamily: brandConfig.fontFamily, color: brandConfig.colors.primary }}
        >
          Passez au prochain niveau !
        </Typography>
        <ButtonField
          variant="outlined"
          label="Suivant"
          onClick={() => {
            onClose();
            onNext?.();
          }}
        />
      </Box>
    );

  return (
    <DialogLayout
      open={open}
      handleClose={onClose}
      title={`🥳 Félicitations ! Vous avez complété le Niveau ${currentLevelNum} : ${currentLevel ? `${currentLevel.name} ${currentLevel.emoji}` : "actuel"}`}
      text={null}
      maxWidth="md"
      customBtnValid={footer}
    >
      <Box sx={{ display: "flex", gap: 2 }} className="flex items-center">
        {/* Left content */}
        <Box sx={{ flex: 1, display: "flex", flexDirection: "column", gap: 2 }}>
          <Typography variant="body1" sx={{ fontFamily: brandConfig.fontFamily, color: "text.secondary" }}>
            Tu viens de compléter le {LEVEL_ORDINALS[currentLevelNum] ?? "dernier"} niveau sur Teach'r !{" "}
            <br /> Pour cela, nous t'offrons la possibilité de bénéficier d'une récompense :
          </Typography>

          {currentLevelNum === 1 && (
            <>
              <Box sx={{ p: 2, borderRadius: 2, bgcolor: "grey.50", border: "1px solid", borderColor: "divider" }}>
                <Typography variant="subtitle1" fontWeight={700} sx={{ fontFamily: brandConfig.fontFamily, color: brandConfig.colors.primary }}>
                  📄 Le guide PDF indispensable pour booster ton nombre d'élèves
                </Typography>
              </Box>
              <Box sx={{ display: "flex", gap: 2 }}>
                <ButtonField
                  variant="contained"
                  label="Visualiser"
                  onClick={() => rewardPdfUrl && window.open(rewardPdfUrl, "_blank")}
                />
                <ButtonField
                  variant="outlined"
                  label="Télécharger"
                  onClick={() => {
                    if (!rewardPdfUrl) return;
                    const a = document.createElement("a");
                    a.href = rewardPdfUrl;
                    a.download = "guide-eleves.pdf";
                    a.click();
                  }}
                />
              </Box>
            </>
          )}

          {currentLevelNum === 2 && (
            <>
              <Box sx={{ p: 2, borderRadius: 2, bgcolor: "grey.50", border: "1px solid", borderColor: "divider" }}>
                <Typography variant="subtitle1" fontWeight={700} sx={{ fontFamily: brandConfig.fontFamily, color: brandConfig.colors.primary }}>
                  💻 La création gratuite de votre site Internet avec SiteFacile
                </Typography>
              </Box>
              <Box>
                <ButtonField variant="contained" label="Créer maintenant" onClick={onClose} />
              </Box>
            </>
          )}

          {currentLevelNum === 3 && (
            <>
              <Box sx={{ p: 2, borderRadius: 2, bgcolor: "grey.50", border: "1px solid", borderColor: "divider" }}>
                <Typography variant="subtitle1" fontWeight={700} sx={{ fontFamily: brandConfig.fontFamily, color: brandConfig.colors.primary }}>
                  📣 Une campagne publicitaire pour votre activité sur nos réseaux sociaux (Instagram et Facebook – 23K abonnés) avec un budget de 30€
                </Typography>
              </Box>
              <Box>
                <ButtonField variant="contained" label="Créer maintenant" onClick={onClose} />
              </Box>
            </>
          )}

          {currentLevelNum === 4 && (
            <>
              <Typography variant="body2" color="text.secondary" sx={{ fontFamily: brandConfig.fontFamily }}>
                Vous avez le choix entre une seule de ces récompenses :
              </Typography>
              <RadioGroup value={selectedReward} onChange={(e) => setSelectedReward(e.target.value)}>
                <FormControlLabel
                  value="meta_ads"
                  control={<Radio />}
                  label={
                    <Typography sx={{ fontFamily: brandConfig.fontFamily }}>
                      📞 <strong>Un budget de 150€ de publicité Meta Ads</strong>
                    </Typography>
                  }
                />
                <FormControlLabel
                  value="amazon"
                  control={<Radio />}
                  label={
                    <Typography sx={{ fontFamily: brandConfig.fontFamily }}>
                      💳 <strong>Une carte cadeau Amazon d'une valeur de 150€</strong>
                    </Typography>
                  }
                />
                <FormControlLabel
                  value="retro_commission"
                  control={<Radio />}
                  label={
                    <Typography sx={{ fontFamily: brandConfig.fontFamily }}>
                      💰 <strong>Une rétro-commission de 150€</strong>
                    </Typography>
                  }
                />
              </RadioGroup>
              <Box>
                <ButtonField
                  variant="contained"
                  label="Choisir"
                  onClick={() => { if (selectedReward) onClose(); }}
                />
              </Box>
            </>
          )}

          {currentLevelNum === 5 && (
            <Box sx={{ p: 2, borderRadius: 2, bgcolor: "grey.50", border: "1px solid", borderColor: "divider" }}>
              <Typography variant="subtitle1" fontWeight={700} sx={{ fontFamily: brandConfig.fontFamily, color: brandConfig.colors.primary }}>
                📉 Une réduction des frais de 2 %
              </Typography>
            </Box>
          )}

          {currentLevelNum === 6 && (
            <Box sx={{ p: 2, borderRadius: 2, bgcolor: "grey.50", border: "1px solid", borderColor: "divider" }}>
              <Typography variant="subtitle1" fontWeight={700} sx={{ fontFamily: brandConfig.fontFamily, color: brandConfig.colors.primary }}>
                📉 Une réduction des frais de 2,5 %
              </Typography>
            </Box>
          )}

          {currentLevelNum === 7 && (
            <>
              <Box sx={{ p: 2, borderRadius: 2, bgcolor: "grey.50", border: "1px solid", borderColor: "divider" }}>
                <Typography variant="subtitle1" fontWeight={700} sx={{ fontFamily: brandConfig.fontFamily, color: brandConfig.colors.primary }}>
                  📉 Une réduction des frais de 3 %
                </Typography>
              </Box>
              <Box>
                <ButtonField variant="contained" label="En bénéficier maintenant" onClick={onClose} />
              </Box>
            </>
          )}
        </Box>

        {/* Right illustration */}
        <Box
          sx={{
            width: { xs: 0, sm: 360 },
            display: { xs: "none", sm: "flex" },
            alignItems: "center",
            justifyContent: "center",
            bgcolor: "grey.50",
            borderRadius: 2,
            userSelect: "none",
          }}
        >
          <img src={pictureAward} alt="image_award" />
        </Box>
      </Box>
    </DialogLayout>
  );
};

export default RewardDialog;
